<template>
  <teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay with more transparency -->
        <div
          class="fixed inset-0 bg-mocha-mousse-900/40 backdrop-blur-sm transition-opacity"
          @click="closeModal"
        ></div>

        <!-- Modal panel with semi-transparent background -->
        <div
          class="inline-block align-bottom bg-white/95 backdrop-blur-md rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full border border-white/20"
        >
          <div class="bg-white/80 backdrop-blur-sm px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Icon icon="mdi:account-multiple" class="text-mocha-mousse-600" />
                {{ modalTitle }}
              </h3>
              <button
                @click="closeModal"
                class="text-gray-400 hover:text-gray-600 focus:outline-none p-1 hover:bg-gray-100/50 rounded-full transition-colors"
              >
                <Icon icon="mdi:close" class="h-6 w-6" />
              </button>
            </div>

            <div v-if="loading" class="text-center py-12">
              <Icon icon="mdi:loading" class="mx-auto h-12 w-12 text-mocha-mousse-500 animate-spin" />
              <p class="mt-3 text-sm text-gray-600">Loading users...</p>
            </div>

            <div v-else-if="userList.length === 0" class="text-center py-12">
              <Icon icon="mdi:account-off" class="mx-auto h-16 w-16 text-gray-400" />
              <h3 class="mt-4 text-lg font-medium text-gray-900">No users found</h3>
              <p class="mt-2 text-sm text-gray-500">No users match the selected criteria</p>
            </div>

            <div v-else class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200/50">
                <thead class="bg-mocha-mousse-50/50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      User
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Role
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      First Connection
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Last Connection
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Visit Count
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white/40 divide-y divide-gray-200/50">
                  <tr v-for="user in userList" :key="user.vuo_id" class="hover:bg-mocha-mousse-50/30 transition-colors">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <div class="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-mocha-mousse-100 to-mocha-mousse-200 rounded-full flex items-center justify-center">
                          <Icon icon="mdi:account" class="h-5 w-5 text-mocha-mousse-600" />
                        </div>
                        <div class="ml-4">
                          <div class="text-sm font-medium text-gray-900">
                            {{ user.vuo_username }}
                          </div>
                          <div class="text-sm text-gray-500">
                            ID: {{ user.vuo_id }}
                          </div>
                          <div v-if="user.vuo_email" class="text-xs text-gray-400">
                            {{ user.vuo_email }}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span
                        :class="getRoleClass(user.enodo_role)"
                        class="inline-flex px-3 py-1 text-xs font-semibold rounded-full"
                      >
                        {{ user.enodo_role || 'user' }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {{ formatDate(user.first_connection) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {{ formatDate(user.last_connection) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {{ user.visit_counts || 0 }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        v-if="user.enodo_role === 'admin'"
                        @click="toggleAdminRole(user, false)"
                        class="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                        title="Remove admin rights"
                      >
                        <Icon icon="mdi:shield-off" class="h-4 w-4 mr-1" />
                        Remove
                      </button>
                      <button
                        v-else
                        @click="toggleAdminRole(user, true)"
                        class="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
                        title="Make admin"
                      >
                        <Icon icon="mdi:shield-plus" class="h-4 w-4 mr-1" />
                        Make Admin
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="bg-mocha-mousse-50/50 backdrop-blur-sm px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200/50">
            <button
              @click="closeModal"
              type="button"
              class="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-6 py-2.5 bg-mocha-mousse-600 text-base font-medium text-white hover:bg-mocha-mousse-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mocha-mousse-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref } from 'vue'
import { Icon } from '@iconify/vue'
import api from '@/services/api'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  userList: {
    type: Array,
    default: () => []
  },
  modalTitle: {
    type: String,
    default: 'User List'
  }
})

const emit = defineEmits(['close', 'refresh'])

const loading = ref(false)

const closeModal = () => {
  emit('close')
}

const formatDate = (dateString) => {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleDateString()
}

const getRoleClass = (role) => {
  switch (role) {
    case 'admin':
      return 'bg-purple-100 text-purple-800'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

const toggleAdminRole = async (user, makeAdmin) => {
  if (!confirm(`Are you sure you want to ${makeAdmin ? 'make' : 'remove admin rights from'} ${user.vuo_username}?`)) {
    return
  }

  try {
    loading.value = true
    await api.updateUserRole(user.vuo_id, makeAdmin ? 'add_admin' : 'remove_admin')

    // Emit refresh to update the user list
    emit('refresh')

  } catch (error) {
    console.error('Failed to update user role:', error)
    alert('Failed to update user role. Please try again.')
  } finally {
    loading.value = false
  }
}
</script>
